import { Controller } from "@hotwired/stimulus"

/**
 * Shows the fields that belong to the chosen recurrence frequency.
 *
 * Weekly asks which days of the week; monthly asks for a date instead — either
 * a day number ("the 15th") or an ordinal weekday ("the first Wednesday").
 * Showing both at once invites a rule that reads as two contradictory things,
 * and the expander only honours one of them.
 *
 * The monthly sub-mode picker works the same way: only the inputs the selected
 * mode uses stay on screen.
 *
 * Usage:
 *   <div data-controller="recurrence-fields">
 *     <select data-recurrence-fields-target="frequency"
 *             data-action="change->recurrence-fields#update">…</select>
 *     <div data-recurrence-fields-target="weeklyFields">…</div>
 *     <div data-recurrence-fields-target="monthlyFields">
 *       <input type="radio" name="…[monthly_mode]" value="day_of_month"
 *              data-recurrence-fields-target="monthlyMode"
 *              data-action="change->recurrence-fields#update">
 *       <div data-recurrence-fields-target="dayOfMonthFields">…</div>
 *       <div data-recurrence-fields-target="nthWeekdayFields">…</div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    "frequency",
    "weeklyFields",
    "monthlyFields",
    "monthlyMode",
    "dayOfMonthFields",
    "nthWeekdayFields",
  ]

  declare readonly hasFrequencyTarget: boolean
  declare readonly frequencyTarget: HTMLSelectElement
  declare readonly hasWeeklyFieldsTarget: boolean
  declare readonly weeklyFieldsTarget: HTMLElement
  declare readonly hasMonthlyFieldsTarget: boolean
  declare readonly monthlyFieldsTarget: HTMLElement
  declare readonly monthlyModeTargets: HTMLInputElement[]
  declare readonly hasDayOfMonthFieldsTarget: boolean
  declare readonly dayOfMonthFieldsTarget: HTMLElement
  declare readonly hasNthWeekdayFieldsTarget: boolean
  declare readonly nthWeekdayFieldsTarget: HTMLElement

  connect() {
    this.update()
  }

  update() {
    const monthly = this.frequency === "monthly"

    if (this.hasWeeklyFieldsTarget) this.weeklyFieldsTarget.classList.toggle("hidden", monthly)
    if (this.hasMonthlyFieldsTarget) this.monthlyFieldsTarget.classList.toggle("hidden", !monthly)

    const byWeekday = this.monthlyMode === "nth_weekday"
    if (this.hasDayOfMonthFieldsTarget) {
      this.dayOfMonthFieldsTarget.classList.toggle("hidden", byWeekday)
    }
    if (this.hasNthWeekdayFieldsTarget) {
      this.nthWeekdayFieldsTarget.classList.toggle("hidden", !byWeekday)
    }
  }

  private get frequency(): string {
    return this.hasFrequencyTarget ? this.frequencyTarget.value : ""
  }

  private get monthlyMode(): string {
    return this.monthlyModeTargets.find(input => input.checked)?.value || "day_of_month"
  }
}
